import { PaletteMode } from '@mui/material';

/** localStorage key. Origin-scoped, so staging and production do not share it. */
export const COLOR_MODE_STORAGE_KEY = 'elevator-color-mode';

/**
 * Cookie name for this deploy.
 * Production uses the bare name with Domain=elevatormag.com, and that cookie is
 * sent to every subdomain, including staging. Staging uses its own name so the
 * production value cannot overwrite a light/dark choice on alpha.
 */
export const colorModeCookieName = (
  cookieDomain: string | undefined = process.env.COOKIE_DOMAIN,
): string => {
  if (
    !cookieDomain ||
    cookieDomain === 'localhost' ||
    cookieDomain === '127.0.0.1' ||
    cookieDomain === 'elevatormag.com'
  ) {
    return COLOR_MODE_STORAGE_KEY;
  }

  return `${COLOR_MODE_STORAGE_KEY}.${cookieDomain}`;
};

const isColorMode = (value: string): value is PaletteMode =>
  value === 'light' || value === 'dark';

/** Every light/dark value stored under this exact cookie name. */
export const readColorModeCookieValues = (
  cookieHeader: string,
  name: string = colorModeCookieName(),
): PaletteMode[] =>
  cookieHeader
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.startsWith(`${name}=`))
    .map((part) => part.slice(name.length + 1).trim())
    .filter(isColorMode);

export const readColorModeFromCookieHeader = (
  cookieHeader: string,
  name: string = colorModeCookieName(),
): PaletteMode | null => {
  const values = readColorModeCookieValues(cookieHeader, name);
  return values[values.length - 1] ?? null;
};

/**
 * Blocking script for `_document`. Applies the saved mode before first paint.
 * Falls back to localStorage when this deploy's cookie is missing, and does not
 * read a same-named cookie from a parent domain.
 */
export const colorModeBlockingScript = ({
  initialMode,
  lightBackground,
  darkBackground,
}: {
  initialMode: PaletteMode;
  lightBackground: string;
  darkBackground: string;
}): string => {
  const cookieName = colorModeCookieName();
  const storageKey = COLOR_MODE_STORAGE_KEY;

  return `
    (function() {
      try {
        var cookieName = '${cookieName}';
        var storageKey = '${storageKey}';
        function readValues(name) {
          var values = [];
          document.cookie.split(';').forEach(function(part) {
            var item = part.trim();
            var prefix = name + '=';
            if (item.indexOf(prefix) === 0) {
              var value = item.slice(prefix.length).trim();
              if (value === 'light' || value === 'dark') values.push(value);
            }
          });
          return values;
        }
        var values = readValues(cookieName);
        if (values.length > 1) {
          document.cookie = cookieName + '=; path=/; max-age=0';
          values = readValues(cookieName);
        }
        var mode = values.length ? values[values.length - 1] : null;
        if (mode !== 'light' && mode !== 'dark') {
          try {
            var stored = localStorage.getItem(storageKey);
            if (stored === 'light' || stored === 'dark') mode = stored;
          } catch (e) {}
        }
        if (mode !== 'light' && mode !== 'dark') mode = '${initialMode}';
        document.documentElement.setAttribute('data-color-mode', mode);
        var bgColor = mode === 'dark' ? '${darkBackground}' : '${lightBackground}';
        document.documentElement.style.backgroundColor = bgColor;
        if (document.body) {
          document.body.style.backgroundColor = bgColor;
        } else {
          document.addEventListener('DOMContentLoaded', function() {
            document.documentElement.style.backgroundColor = bgColor;
            if (document.body) document.body.style.backgroundColor = bgColor;
          });
        }
      } catch (e) {
        document.documentElement.setAttribute('data-color-mode', '${initialMode}');
        if (document.body) {
          document.body.style.backgroundColor = '${initialMode === 'dark' ? darkBackground : lightBackground}';
        }
      }
    })();
  `;
};
